import { AuthService } from '../services/auth';

/**
 * Admin Setup Script
 * 
 * This script creates the initial admin user for the Wote InterCity admin dashboard.
 * Run this script once to set up the admin@woteintercity.com account.
 * 
 * Usage:
 * 1. Import and call setupAdminUser() in your app initialization
 * 2. Or run this script manually during deployment
 */

export const setupAdminUser = async (): Promise<{
  success: boolean;
  message: string;
  user?: any;
}> => {
  try {
    console.log('🚀 Setting up admin user...');
    
    // First, try to sign in with existing admin user
    console.log('🔍 Checking if admin user already exists...');
    try {
      const signInResult = await AuthService.signIn('admin@woteintercity.com', 'admin123');
      if (signInResult.success) {
        console.log('✅ Admin user exists and signed in successfully');
        return {
          success: true,
          message: 'Admin user exists and signed in successfully',
          user: signInResult.user
        };
      }
    } catch (signInError) {
      console.log('ℹ️ Admin user does not exist or sign in failed, creating new user...');
    }

    // If sign in failed, create admin user
    console.log('📝 Creating new admin user...');
    const result = await AuthService.createAdminUser(
      'admin@woteintercity.com',
      'admin123',
      'Admin',
      'User',
      '+233123456789',
      'super_admin'
    );

    if (result.success) {
      console.log('✅ Admin user created successfully');
      return {
        success: true,
        message: 'Admin user created successfully',
        user: result.user
      };
    } else {
      console.error('❌ Failed to create admin user:', result.error);
      return {
        success: false,
        message: result.error || 'Failed to create admin user'
      };
    }
  } catch (error: any) {
    console.error('❌ Admin setup error:', error);
    return {
      success: false,
      message: error.message || 'Admin setup failed'
    };
  }
};

/**
 * Initialize admin user if not exists
 * Call this function in your app's main component or initialization
 */
export const initializeAdmin = async () => {
  try {
    const result = await setupAdminUser();
    
    if (result.success) {
      console.log('🎉 Admin initialization complete:', result.message);
    } else {
      console.warn('⚠️ Admin initialization failed:', result.message);
    }
    
    return result;
  } catch (error) {
    console.error('💥 Admin initialization error:', error);
    return {
      success: false,
      message: 'Admin initialization failed'
    };
  }
};

/**
 * Admin credentials for reference
 */
export const ADMIN_CREDENTIALS = {
  email: 'admin@woteintercity.com',
  password: 'admin123',
  role: 'super_admin',
  permissions: [
    'users:read',
    'users:write',
    'bookings:read',
    'bookings:write',
    'routes:read',
    'routes:write',
    'buses:read',
    'buses:write',
    'analytics:read',
    'analytics:write'
  ]
};

/**
 * Verify admin access
 * Use this to check if the current user has admin privileges
 */
export const verifyAdminAccess = async (): Promise<boolean> => {
  try {
    const user = await AuthService.getCurrentUser();
    return user?.role === 'admin' || user?.role === 'super_admin';
  } catch (error) {
    console.error('Admin access verification failed:', error);
    return false;
  }
};
